import { Input, type InputProps } from './Input.js'

export interface MoneyInputProps extends Omit<
  InputProps,
  'value' | 'onChange' | 'leading' | 'type'
> {
  /** **Cents.** What the API takes, and what this holds. */
  value: number | null
  onChange: (cents: number | null) => void
  currency?: string
}

/**
 * Money, in cents, typed in dollars.
 *
 * The API takes cents everywhere — `price_cents`, `deposit_cents`,
 * `booking_fee_cents` — and a person types `25.00`. Doing that conversion at
 * seventeen call sites is seventeen chances to be out by a factor of a hundred on
 * a screen that moves real money, and the failure is silent: `2500` and `25` are
 * both plausible prices.
 *
 * So the component holds cents and shows dollars, and nothing above it converts.
 */
export function MoneyInput({ value, onChange, currency = '$', ...props }: MoneyInputProps) {
  return (
    <Input
      inputMode="decimal"
      leading={<span className="text-body">{currency}</span>}
      value={value === null ? '' : (value / 100).toFixed(2)}
      onChange={(e) => {
        const raw = e.target.value.trim()
        if (raw === '') return onChange(null)
        const parsed = Number(raw.replace(/[^0-9.]/g, ''))
        if (Number.isNaN(parsed)) return
        // Rounded, not truncated: 19.99 in floating point is 1998.9999…, and
        // truncating turns a price into a penny less every time it is saved.
        onChange(Math.round(parsed * 100))
      }}
      {...props}
    />
  )
}
